/**
 * Loading a serial by its folder. Ruling 71, 12 October 2026.
 *
 * One action: the trainee picks the folder, and ATLas takes the English
 * warning order, the orders, the trace image and the sidecar out of it. He
 * does not pick three or four files out of six and hope he got the set he
 * meant.
 *
 * File selection stays as the fallback and is not removed. A folder picker is
 * one browser control among several and the one most likely to be refused or
 * absent, and a trainee who cannot load his orders has no training session.
 *
 * **Everything left out is named, with why.** A folder holds more than a
 * serial — the French half, the sidecar's own backups, whatever else sits
 * beside them — and a loader that silently drops files is a loader that can
 * silently drop the wrong one.
 */

import { classifyOrderSetFile } from "./classify";
import type { OrderSetFileInput } from "./types";

/**
 * Doc 04 section 5.5 and Ruling 71: the French files are ignored until the
 * French serial has a sidecar of its own. The trace overlay is bound to one
 * sidecar, and the French serial carries different grids and different
 * timings, so the English sidecar does not describe it.
 *
 * One line, said to the trainee as-is.
 */
export const FRENCH_IGNORED_LINE =
  "The French files in that folder were not loaded. The French serial needs a sidecar of its own before it can be delivered, and the English one does not describe it.";

/** A file the folder held and the loader did not take, and why. */
export interface IgnoredFolderFile {
  readonly name: string;
  readonly reason: string;
}

export interface FolderSelection {
  /** True where at least one French-layer document was set aside. */
  readonly frenchPresent: boolean;
  readonly ignored: readonly IgnoredFolderFile[];
  /** The serial's own files, for `resolveOrderSetSelection`. */
  readonly selected: readonly OrderSetFileInput[];
  /** The sidecar, where the folder holds exactly one. */
  readonly sidecar: OrderSetFileInput | null;
}

const SIDECAR = /\.json$/i;
/**
 * A backup written beside the file it came from, by this project's own
 * convention: `<name>.before-r<ruling>`. It is a previous version of a file
 * in the same folder and must never be loaded in place of it.
 */
const BACKUP = /\.before-r\d+$/i;

function leafOf(name: string): string {
  const parts = name.split(/[/\\]/);
  /* v8 ignore next -- a split always yields at least one part */
  return parts[parts.length - 1] ?? name;
}

/**
 * The serial inside a folder.
 *
 * Names arrive as relative paths from a directory picker, so each is reduced
 * to its leaf before anything looks at it — no path is carried into
 * application state, which is the rule `ClassifiedOrderSetFile` already
 * keeps.
 */
export function selectSerialFromFolder(
  files: readonly OrderSetFileInput[],
): FolderSelection {
  const ignored: IgnoredFolderFile[] = [];
  const selected: OrderSetFileInput[] = [];
  const sidecars: OrderSetFileInput[] = [];
  let frenchPresent = false;

  for (const file of files) {
    const name = leafOf(file.name);
    const entry = { name: file.name, text: file.text };

    if (BACKUP.test(name)) {
      ignored.push({ name, reason: "a backup of another file in the folder" });
      continue;
    }
    if (SIDECAR.test(name)) {
      sidecars.push(entry);
      continue;
    }

    const classified = classifyOrderSetFile({ name, text: file.text });
    if (classified.languageLayer === "fr") {
      frenchPresent = true;
      ignored.push({ name, reason: "the French layer of this serial" });
      continue;
    }
    if (classified.role === "unknown") {
      ignored.push({ name, reason: "not a part of an order set" });
      continue;
    }
    selected.push(entry);
  }

  // More than one sidecar is a choice this must not make. Both are named and
  // neither is used: the overlay is bound to one file, and guessing which
  // would put the wrong markings on the trainee's trace.
  if (sidecars.length > 1) {
    for (const extra of sidecars) {
      ignored.push({
        name: leafOf(extra.name),
        reason: `one of ${String(sidecars.length)} sidecars in the folder, so none was taken`,
      });
    }
  }

  return {
    frenchPresent,
    ignored,
    selected,
    /* v8 ignore next -- a list of length one has a first entry */
    sidecar: sidecars.length === 1 ? (sidecars[0] ?? null) : null,
  };
}
